import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { customerAuthService } from "../services/authService";

const CustomerAuthContext = createContext(null);
export const useCustomerAuth = () => useContext(CustomerAuthContext);

const CUSTOMER_TOKEN_KEY = "cynos_customer_token";
const CUSTOMER_DATA_KEY = "cynos_customer";
const CUSTOMER_RESET_TOKEN_KEY = "cynos_reset_token";
const CUSTOMER_PASSWORD_RESET_TOKEN_KEY = "cynos_password_reset_token";
const CUSTOMER_RESET_IDENTIFIER_KEY = "cynos_reset_identifier";
const CUSTOMER_RESET_DELIVERY_KEY = "cynos_reset_delivery_target";

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem(CUSTOMER_TOKEN_KEY);
    const storedCustomer = localStorage.getItem(CUSTOMER_DATA_KEY);
    if (storedToken && storedCustomer) {
      setToken(storedToken);
      try {
        setCustomer(JSON.parse(storedCustomer));
      } catch {
        localStorage.removeItem(CUSTOMER_DATA_KEY);
      }
    }
    setLoading(false);
  }, []);

  const saveAuth = useCallback((customerData, authToken) => {
    localStorage.setItem(CUSTOMER_TOKEN_KEY, authToken);
    localStorage.setItem(CUSTOMER_DATA_KEY, JSON.stringify(customerData));
    setCustomer(customerData);
    setToken(authToken);
  }, []);

  const clearAuth = () => {
    localStorage.removeItem(CUSTOMER_TOKEN_KEY);
    localStorage.removeItem(CUSTOMER_DATA_KEY);
    localStorage.removeItem(CUSTOMER_RESET_TOKEN_KEY);
    localStorage.removeItem(CUSTOMER_PASSWORD_RESET_TOKEN_KEY);
    localStorage.removeItem(CUSTOMER_RESET_IDENTIFIER_KEY);
    localStorage.removeItem(CUSTOMER_RESET_DELIVERY_KEY);
    setCustomer(null);
    setToken(null);
  };

  const login = async (email, password) => {
    const data = await customerAuthService.login(email, password);
    saveAuth(data.customer, data.token);
    return data;
  };

  const signup = async (name, email, phone, password) => {
    const data = await customerAuthService.signup(name, email, phone, password);
    saveAuth(data.customer, data.token);
    return data;
  };

  const googleLogin = async (redirectAfter = "/profile") => {
    const data = await customerAuthService.googleUrl(redirectAfter);
    window.location.href = data.auth_url;
  };

  const completeGoogleLogin = async (code, state) => {
    const data = await customerAuthService.completeGoogleLogin(code, state);
    saveAuth(data.customer, data.token);
    return data;
  };

  const logout = () => {
    clearAuth();
  };

  const refreshCustomer = useCallback(async () => {
    const data = await customerAuthService.me();
    const authToken = token || localStorage.getItem(CUSTOMER_TOKEN_KEY);
    if (authToken) saveAuth(data, authToken);
    return data;
  }, [saveAuth, token]);

  const updateCustomer = useCallback(async (payload) => {
    const data = await customerAuthService.updateMe(payload);
    const authToken = token || localStorage.getItem(CUSTOMER_TOKEN_KEY);
    if (authToken) saveAuth(data, authToken);
    return data;
  }, [saveAuth, token]);

  const forgotPassword = async (identifier) => {
    const data = await customerAuthService.forgotPassword(identifier);
    localStorage.setItem(CUSTOMER_RESET_IDENTIFIER_KEY, identifier);
    if (data.reset_token) {
      localStorage.setItem(CUSTOMER_RESET_TOKEN_KEY, data.reset_token);
    }
    if (data.delivery_target) {
      localStorage.setItem(CUSTOMER_RESET_DELIVERY_KEY, data.delivery_target);
    }
    return data;
  };

  const verifyOtp = async (otp) => {
    const resetToken = localStorage.getItem(CUSTOMER_RESET_TOKEN_KEY);
    if (!resetToken) {
      throw new Error("Reset token is missing. Please start password recovery again.");
    }
    const data = await customerAuthService.verifyOtp(resetToken, otp);
    if (data.password_reset_token) {
      localStorage.setItem(CUSTOMER_PASSWORD_RESET_TOKEN_KEY, data.password_reset_token);
    }
    return data;
  };

  const resetPassword = async (newPassword) => {
    const passwordResetToken = localStorage.getItem(CUSTOMER_PASSWORD_RESET_TOKEN_KEY);
    if (!passwordResetToken) {
      throw new Error("Password reset token is missing. Please verify your OTP first.");
    }
    const data = await customerAuthService.resetPassword(passwordResetToken, newPassword);
    localStorage.removeItem(CUSTOMER_RESET_TOKEN_KEY);
    localStorage.removeItem(CUSTOMER_PASSWORD_RESET_TOKEN_KEY);
    localStorage.removeItem(CUSTOMER_RESET_DELIVERY_KEY);
    return data;
  };

  return (
    <CustomerAuthContext.Provider
      value={{
        customer,
        token,
        loading,
        isAuthenticated: Boolean(customer),
        login,
        signup,
        googleLogin,
        completeGoogleLogin,
        logout,
        refreshCustomer,
        updateCustomer,
        forgotPassword,
        verifyOtp,
        resetPassword,
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
};
